import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, Phone, Search, X } from "lucide-react";
import { NAV_LINKS, SERVICES, SITE } from "../data/site";
import SearchOverlay from "./SearchOverlay";
import ScrollProgress from "./ScrollProgress";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const megaTimeout = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen, searchOpen]);

  const openMega = () => {
    clearTimeout(megaTimeout.current);
    setMegaOpen(true);
  };
  const closeMega = () => {
    megaTimeout.current = setTimeout(() => setMegaOpen(false), 120);
  };

  const isActive = (path) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
          scrolled ? "shadow-[0_2px_24px_rgba(10,12,15,0.12)]" : "border-b border-gig-steel/15"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 lg:px-10">
          <Link to="/" className="flex-shrink-0 py-3" data-testid="header-logo" aria-label="Global Industry Group home">
            <img
              src={SITE.logo}
              alt="Global Industry Group"
              className={`w-auto transition-all duration-300 ${scrolled ? "h-9" : "h-11"}`}
              width="2000"
              height="412"
            />
          </Link>

          <nav aria-label="Primary" className="hidden xl:block">
            <ul className="flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <li
                  key={link.path}
                  className="relative"
                  onMouseEnter={link.dropdown ? openMega : undefined}
                  onMouseLeave={link.dropdown ? closeMega : undefined}
                >
                  <NavLink
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`group relative flex items-center gap-1 px-3 py-5 font-condensed text-[15px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                      isActive(link.path) ? "text-gig-red" : "text-gig-black hover:text-gig-red"
                    }`}
                    aria-expanded={link.dropdown ? megaOpen : undefined}
                    aria-haspopup={link.dropdown ? "true" : undefined}
                    onFocus={link.dropdown ? openMega : undefined}
                  >
                    {link.label}
                    {link.dropdown && (
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                    )}
                    <span
                      className={`absolute bottom-2.5 left-3 right-3 h-0.5 bg-gig-red transition-transform duration-300 origin-left ${
                        isActive(link.path) ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                      aria-hidden="true"
                    />
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="open-search-button"
              aria-label="Search this site"
              className="hidden sm:flex h-10 w-10 items-center justify-center text-gig-black hover:text-gig-red transition-colors"
            >
              <Search className="h-5 w-5" aria-hidden="true" />
            </button>
            <Link
              to="/contact"
              data-testid="header-quote-cta"
              className="btn-primary hidden md:inline-flex !px-6 !py-2.5"
            >
              Request a Quote
              <ArrowRight className="btn-arrow h-4 w-4" aria-hidden="true" />
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="xl:hidden flex h-11 w-11 items-center justify-center text-gig-black"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Services mega menu */}
        <AnimatePresence>
          {megaOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              onMouseEnter={openMega}
              onMouseLeave={closeMega}
              className="absolute inset-x-0 top-full hidden xl:block border-t border-gig-steel/15 bg-white shadow-[0_30px_60px_rgba(10,12,15,0.18)]"
              data-testid="services-mega-menu"
            >
              <div className="mx-auto grid max-w-[1440px] grid-cols-[280px_1fr] gap-10 px-10 py-8">
                <div>
                  <p className="eyebrow">What We Do</p>
                  <p className="mt-4 font-condensed text-3xl font-bold uppercase leading-[0.95] text-gig-black">
                    Integrated Industrial Services
                  </p>
                  <p className="mt-3 text-sm text-gig-steel">
                    Nine self-performed disciplines delivered through one coordinated team.
                  </p>
                  <Link
                    to="/services"
                    className="mt-5 inline-flex items-center gap-2 font-condensed text-sm font-semibold uppercase tracking-[0.14em] text-gig-red hover:gap-3 transition-all"
                    data-testid="mega-menu-all-services"
                  >
                    View All Services <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`mega-menu-${s.slug}`}
                      className="group flex items-center gap-3 border-b border-gig-steel/10 py-3 hover:border-gig-red/40 transition-colors"
                    >
                      <img
                        src={s.image.replace(".webp", "-900.webp")}
                        alt=""
                        className="h-11 w-14 flex-shrink-0 object-cover"
                        loading="lazy"
                        width="56"
                        height="44"
                      />
                      <span className="font-condensed text-lg font-semibold uppercase leading-tight text-gig-black group-hover:text-gig-red transition-colors">
                        {s.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-gig-black/60 xl:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <motion.nav
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-gig-black text-white"
              aria-label="Mobile"
              data-testid="mobile-menu"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-white/10 p-5">
                <img src={SITE.logoWhite} alt="Global Industry Group" className="h-8 w-auto" width="2000" height="412" />
                <button
                  type="button"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  onClick={() => setMobileOpen(false)}
                  className="flex h-10 w-10 items-center justify-center text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <ul className="divide-y divide-white/10">
                  {NAV_LINKS.map((link) =>
                    link.dropdown ? (
                      <li key={link.path}>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between py-4 font-condensed text-xl font-semibold uppercase tracking-[0.08em]"
                          aria-expanded={mobileServicesOpen}
                          data-testid="mobile-services-toggle"
                          onClick={() => setMobileServicesOpen((v) => !v)}
                        >
                          Services
                          <ChevronDown className={`h-5 w-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                        </button>
                        <AnimatePresence initial={false}>
                          {mobileServicesOpen && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden pl-4"
                            >
                              <li>
                                <Link to="/services" className="block py-2.5 font-condensed text-base uppercase tracking-[0.1em] text-gig-red" data-testid="mobile-nav-services-overview">
                                  All Services
                                </Link>
                              </li>
                              {SERVICES.map((s) => (
                                <li key={s.slug}>
                                  <Link
                                    to={`/services/${s.slug}`}
                                    className="block py-2.5 font-condensed text-base uppercase tracking-[0.1em] text-white/80"
                                    data-testid={`mobile-nav-${s.slug}`}
                                  >
                                    {s.name}
                                  </Link>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </li>
                    ) : (
                      <li key={link.path}>
                        <Link
                          to={link.path}
                          data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`block py-4 font-condensed text-xl font-semibold uppercase tracking-[0.08em] ${
                            isActive(link.path) ? "text-gig-red" : "text-white"
                          }`}
                        >
                          {link.label}
                        </Link>
                      </li>
                    )
                  )}
                </ul>
                <button
                  type="button"
                  className="mt-4 flex items-center gap-2 text-white/80"
                  data-testid="mobile-open-search"
                  onClick={() => {
                    setMobileOpen(false);
                    setSearchOpen(true);
                  }}
                >
                  <Search className="h-5 w-5" /> Search this site
                </button>
              </div>
              <div className="border-t border-white/10 p-5 space-y-3">
                <Link to="/contact" className="btn-primary w-full" data-testid="mobile-quote-cta">
                  Request a Quote
                </Link>
                <a href={SITE.phoneHref} className="btn-outline-light w-full" data-testid="mobile-call-cta">
                  <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}
                </a>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} onNavigate={(p) => { setSearchOpen(false); navigate(p); }} />
      <ScrollProgress />
    </>
  );
}
